<template>
    <div :class="['forgot-password', { modal: !isLoginPage, page: isLoginPage }]">
        <h2 class="forgot-password__title">
            {{ $t('modals.forgot_password.heading') }}
        </h2>

        <template v-if="!resetSuccessful">
            <p class="forgot-password__description">
                {{ $t('modals.forgot_password.description') }}
            </p>

            <form class="form" @submit.prevent>
                <div class="form__row">
                    <label class="label">
                        {{ $t('form_inputs.email_address_label') }}
                    </label>
                    <input
                        v-model.trim="formData.email"
                        class="input"
                        :class="{ 'input--error': $v.formData.email.$invalid && $v.formData.email.$dirty }"
                        type="email"
                    />
                    <InputError v-if="!$v.formData.email.required && $v.formData.email.$dirty" static-position>
                        {{ $t('errors.field_required') }}
                    </InputError>
                    <InputError v-if="!$v.formData.email.email && $v.formData.email.$dirty" static-position>
                        {{ $t('errors.field_invalid') }}
                    </InputError>
                </div>
                <recaptcha v-if="$config.recaptcha.enabled" />

                <Notice v-if="resetError" type="warning">
                    <template v-if="resetError.slug === 'recaptcha_verification_failed'">
                        {{ $t('sections.rent_out_hero.support_form.recaptcha_verification_failed') }}
                    </template>
                    <template v-else>
                        {{ $t('errors.an_error_occurred') }}
                        {{ $t('errors.try_again_or_contact_us') }}
                    </template>
                </Notice>
                <div class="form__row">
                    <RecaptchaButton
                        :disabled="isLoading"
                        :loading="isLoading"
                        :is-invalid="$v.formData.$invalid && $v.formData.$dirty"
                        :label="$t('modals.forgot_password.reset_password_button')"
                        @start-verifying="onStartVerifying"
                        @error="isLoading = false"
                        @success="determineNextStepAfterEmailCheck"
                    />
                </div>
            </form>
            <a v-if="displayBackLink" @click.prevent="$emit('close-modal')">
                {{ $t('modals.forgot_password.back_to_login_link') }}
            </a>
        </template>

        <template v-else>
            <p class="forgot-password__description">
                {{ $t('modals.forgot_password.success_notice') }}
            </p>

            <button class="button button--primary button--full-width" @click="handleBackBtn">
                {{ $t('modals.forgot_password.back_to_login_link') }}
            </button>
        </template>
    </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import modalMixin from '~/mixins/modalMixin';
import { FACEBOOK, PASSWORD, GOOGLE } from '~/config/authMethods';
import Notice from '~/components/Notice.vue';
import InputError from '~/components/form-elements/InputError';
import RecaptchaButton from '~/components/form-elements/RecaptchaButton';

export default {
    components: {
        Notice,
        InputError,
        RecaptchaButton,
    },
    mixins: [validationMixin, modalMixin],
    props: {
        displayBackLink: {
            type: Boolean,
            default: true,
        },
        page: {
            type: String,
            default: '',
        },
    },
    validations: {
        formData: {
            email: {
                required,
                email,
            },
        },
    },
    data() {
        return {
            formData: {
                email: null,
            },
            resetSuccessful: false,
            resetError: null,
            isLoading: false,
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen']),
        isLoginPage() {
            return this.page === 'login';
        },
        isBookPage() {
            return this.page === 'book';
        },
    },
    methods: {
        handleBackBtn() {
            this.$emit('close-modal');
        },
        onStartVerifying() {
            this.isLoading = true;
            this.$v.formData.$touch();
        },
        determineNextStepAfterEmailCheck(token) {
            const data = {
                email: this.formData.email,
                recaptchaToken: token,
            };

            this.isLoading = true;
            this.$authService
                .checkEmail(data)
                .then(async response => {
                    if (response.exists) {
                        if (response.authMethod === PASSWORD) {
                            this.forgotPassword();
                        }

                        if ([FACEBOOK, GOOGLE].includes(response.authMethod)) {
                            if (this.isLoginPage) {
                                return this.$router.push(
                                    this.localePath({
                                        name: 'login',
                                        query: {
                                            email: this.formData.email,
                                            authMethod: response.authMethod,
                                        },
                                    })
                                );
                            }

                            const data = {
                                authMethod: response.authMethod,
                                email: this.formData.email,
                            };

                            if (this.isBookPage) {
                                this.$emit('open-auth-modal', data);
                            } else {
                                this.displayContinueLoginModal(data);
                            }

                            this.isLoading = false;
                        }
                    } else {
                        this.isLoading = false;
                        this.resetSuccessful = true; // show success screen if email is not registered
                        await this.$recaptcha.reset();
                    }
                })
                .catch(async err => {
                    this.$sentry.captureException(err);
                    this.isLoading = false;
                    await this.$recaptcha.reset();
                });
        },
        forgotPassword() {
            this.resetSuccessful = false;
            this.resetError = false;

            this.$authService
                .forgotPassword(this.formData.email)
                .then(() => {
                    this.resetSuccessful = true;
                })
                .catch(error => {
                    this.$sentry.withScope(scope => {
                        scope.setExtra('error', error);

                        this.$sentry.captureMessage(
                            `Forgot password failed with error: ${JSON.stringify(error)}`,
                            'warning'
                        );
                    });

                    this.resetError = {
                        status: 'error',
                        message: error.message,
                        slug: error.slug,
                    };
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.forgot-password {
    &__back-btn {
        position: absolute;
        z-index: 11;

        .svg-icon {
            width: 32px;
            height: 32px;
        }
    }

    &__title {
        @extend %heading-2;
        margin-bottom: 20px;
        text-align: center;
    }

    &.page {
        padding-top: 27px;

        .forgot-password__back-btn {
            top: -5px;
            left: 0;
        }

        @include lg {
            padding-top: 32px;

            .forgot-password__back-btn {
                top: 24px;
                left: 40px;
            }
        }
    }

    &.modal {
        .forgot-password__back-btn {
            left: 0;
            top: -32px;

            @include lg {
                top: -7px;
            }
        }
    }
}
</style>
